import axios, {
  AxiosInstance,
  AxiosResponse,
  AxiosError,
  AxiosRequestConfig,
} from "axios";
import { notification } from "antd";
import { clearLocalStorage, getLocalStorage } from "../../utils/localStorage";
import { trackApiAuthRejected, trackForcedRelogin } from "../../utils/zymedSessionDiagnostics";
import { AUTH_TOKEN } from "../../utils/constants";
import {
  DRLITE_SECRET_KEY,
  USER_MANAGEMENT_API_KEY,
  USER_MANAGEMENT_API_SECRET_KEY,
  VOICE_X_API_KEY
} from "../../envConstants";

// Create an Axios instance with a default base URL if provided
const api: AxiosInstance = axios.create({
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor to handle custom base URL
api.interceptors.request.use(
  (config: AxiosRequestConfig | any) => {
    // Check for custom base URL in the request config
    if (config.customBaseUrl) {
      config.baseURL = config.customBaseUrl;
    }

    // Add Authorization headers if needed
    const token = getLocalStorage(AUTH_TOKEN);
    if (token) {
      config.headers = {
        ...config.headers,
        ...(config.isMyTatva && {
          drlite_secret_key: DRLITE_SECRET_KEY,
          token,
        }),
        ...(config.isTextPlain && {
          "Content-Type": "text/plain",
        }),
        ...(!config.isMyTatva &&
          !config.isTShots && { Authorization: `Bearer ${token}` }),
        ...(config.isTShots && {
          api_key: USER_MANAGEMENT_API_KEY,
          api_secret_key: USER_MANAGEMENT_API_SECRET_KEY,
        }),
        ...(config.isVoice && {
          "X-API-Key": VOICE_X_API_KEY,
        }),
        ...(config.isFormData && {
          "Content-Type": "multipart/formdata",
        }),
      };
    }
    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  }
);

// Full request URL for diagnostics (axios keeps baseURL and url separately).
const requestUrl = (config?: any): string | undefined =>
  config?.url ? (/^https?:/.test(config.url) ? config.url : `${config.baseURL || ""}${config.url}`) : undefined;

const reportAuthRejection = (handler: string, config: any, httpStatus: number | undefined, body: any) => {
  const url = requestUrl(config);
  const auth = config?.headers?.Authorization || config?.headers?.authorization;
  trackApiAuthRejected({
    tokenKind: "auth",
    url,
    httpStatus,
    body,
    token: typeof auth === "string" ? auth.replace(/^Bearer /, "") : localStorage.getItem("authToken"),
    attempt: "first",
  });
  trackForcedRelogin({ handler, url, httpStatus, body });
};

// Response interceptor to handle errors
api.interceptors.response.use(
  (response: AxiosResponse) => {
    if (response.data && response.data.status === 403) {
      reportAuthRejection("api_body_403", response.config, response.status, response.data);
      clearLocalStorage();
      sessionStorage.clear();
      localStorage.removeItem("tatvaPediaToken");
      localStorage.removeItem("tatvaPediaBookmarks");
      localStorage.removeItem("tatvaPediaLikes");
      localStorage.removeItem("tatvaPediaFilters");
      localStorage.removeItem("pediaUserPreferences");
      notification.error({
        message: "Session Expired",
        description: "Let’s get you back in! Re-login for seamless experience",
        duration: 3,
      });
      setTimeout(() => {
        window.location.href = "/on-boarding";
      }, 2000);
      return Promise.reject(new Error("Let’s get you back in! Re-login for seamless experience"));
    }
    
    // Handle successful responses here
    return response;
  },
  (error: AxiosError | any) => {
    // Handle errors globally
    if (error.response) {
      const { status, data } = error.response;

      let message = "An error occurred";
      switch (status) {
        case 400:
          message = "Bad Request: " + (data.message || "Invalid request");
          break;
        case 401:
          message = "Unauthorized: " + (data.message || "Please log in");
          reportAuthRejection("api_http_401", error.config, 401, data);
          clearLocalStorage();
          window.location.href = "/on-boarding";
          break;
        case 403:
          message = "Forbidden: " + (data.message || "Access denied");
          reportAuthRejection("api_http_403", error.config, 403, data);
          clearLocalStorage();
          sessionStorage.clear();
          
          localStorage.removeItem("tatvaPediaToken");
          localStorage.removeItem("tatvaPediaBookmarks");
          localStorage.removeItem("tatvaPediaLikes");
          localStorage.removeItem("tatvaPediaFilters");
          localStorage.removeItem("pediaUserPreferences");
          
          notification.error({
            message: "Let’s get you back in! Re-login for seamless experience",
            description: "Let’s get you back in! Re-login for seamless experience",
            duration: 3,
          });
          
          setTimeout(() => {
            window.location.href = "/on-boarding";
          }, 2000);
          break;
        case 404:
          return;
        case 500:
          message = "Server Error: " + (data.message || "Something went wrong");
          break;
        case 502:
          message = "Bad Gateway";
          break;
        case 508:
          message = "Time Out";
          break;
        default:
          message = "Error: " + (data.message || "An error occurred");
          break;
      }

      notification.error({
        message: `HTTP ${status}`,
        description: message,
        duration: 5,
      });
    }

    return Promise.reject(error);
  }
);

export default api;
